// EnneagramChart — animation primitives, built WITHOUT Framer Motion (the project
// has no animation dependency; this stays dependency-free): a cubic-bezier easing
// evaluator + a requestAnimationFrame value-tween hook. All petal length changes
// (including the initial "grow from center" on mount, which is simply a tween
// from 0 → target) go through this, so geometry is always recomputed from
// smoothly-interpolated numbers — never redrawn instantly.

import { useEffect, useRef, useState } from "react";
import { REDUCE_MOTION } from "../../lib/theme";

/**
 * Evaluates a CSS-style cubic-bezier(x1,y1,x2,y2) easing curve at progress `t`
 * (0..1), solving for the bezier parameter via Newton-Raphson (falls back to
 * bisection) — same curve family CSS `transition-timing-function` uses, so the
 * JS-driven tween and the CSS hover/selected transitions feel identical.
 */
export function cubicBezierEase(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const bezierComponent = (t: number, a: number, b: number) => {
    const c = 3 * a;
    const d = 3 * (b - a) - c;
    const e = 1 - c - d;
    return ((e * t + d) * t + c) * t;
  };
  const bezierDerivative = (t: number, a: number, b: number) => {
    const c = 3 * a;
    const d = 3 * (b - a) - c;
    const e = 1 - c - d;
    return (3 * e * t + 2 * d) * t + c;
  };
  return (t: number) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    let guess = t;
    for (let i = 0; i < 8; i++) {
      const x = bezierComponent(guess, x1, x2) - t;
      const dx = bezierDerivative(guess, x1, x2);
      if (Math.abs(dx) < 1e-6) break;
      const next = guess - x / dx;
      if (!Number.isFinite(next)) break;
      guess = Math.min(1, Math.max(0, next));
    }
    return bezierComponent(guess, y1, y2);
  };
}

/** The chart's standard easing — a soft ease-out, per project animation spec. */
export const ENN_EASE = cubicBezierEase(0.22, 0.61, 0.36, 1);
export const ENN_EASE_CSS = "cubic-bezier(.22,.61,.36,1)";

/**
 * Tweens an array of numbers toward `target` over `durationMs`, using
 * requestAnimationFrame + ENN_EASE. On first mount it animates from an
 * all-zero array (petal length 0 → target = "grow from center"); on
 * subsequent target changes it animates from the last rendered values.
 * Respects prefers-reduced-motion (snaps instantly) and `active === false`.
 */
export function useTweenedValues(target: number[], durationMs = 650, active = true): number[] {
  const skip = !active || REDUCE_MOTION;
  // Lazy initializer only — never reads a ref during render (React refs must
  // only be read from effects/handlers, never render).
  const [tweened, setTweened] = useState<number[]>(() => target.map(() => 0));
  const lastRef = useRef<number[]>(target.map(() => 0));
  const mountedRef = useRef(false);
  const rafRef = useRef<number | undefined>(undefined);
  const targetKey = target.join(",");

  useEffect(() => {
    // `skip` (reduced-motion / not animated) is handled entirely by the
    // return-value ternary below — no setState here, so a disabled/instant
    // chart never triggers an extra effect-driven render.
    if (skip) return;

    const from = mountedRef.current ? lastRef.current : target.map(() => 0);
    mountedRef.current = true;
    const to = target;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = ENN_EASE(t);
      const next = to.map((v, i) => {
        const f = from[i] ?? 0;
        return f + (v - f) * eased;
      });
      lastRef.current = next;
      setTweened(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, durationMs, skip]);

  return skip ? target : tweened;
}
